import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { agentsFetch } from "../api";

/* ─── Wizard step definitions (Welcome is pre-wizard, not listed) ─── */

const BASE_STEPS = [
  { key: "company", label: "Company & Brand", icon: "building" },
  { key: "customers", label: "Your Customers", icon: "users" },
  { key: "voice-workflow", label: "Voice & Workflow", icon: "mic" },
  { key: "connections", label: "Connections", icon: "link" },
  { key: "team", label: "Your Team", icon: "team" },
];

const AGENCY_STEP = { key: "clients", label: "Your Clients", icon: "briefcase" };

/* ─── Step icon ─── */

function StepIcon({ type, active }: { type: string; active: boolean }) {
  const cls = `h-4 w-4 transition-colors duration-150 ${active ? "text-blue-600" : "text-gray-400"}`;
  const p: Record<string, string> = {
    building: "M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21",
    users: "M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z",
    mic: "M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z",
    link: "M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244",
    team: "M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z",
    briefcase: "M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0",
  };
  if (!p[type]) return null;
  return (<svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d={p[type]} /></svg>);
}

/* ─── Integration data ─── */

const INTEGRATIONS = [
  { category: "Meeting Source", items: [
    { id: "zoom", name: "Zoom" }, { id: "gmeet", name: "Google Meet" }, { id: "teams-meet", name: "Microsoft Teams" },
  ]},
  { category: "Document Storage", items: [
    { id: "gdrive", name: "Google Drive" }, { id: "dropbox", name: "Dropbox" }, { id: "onedrive", name: "OneDrive" },
  ]},
  { category: "Project Management", items: [
    { id: "clickup", name: "ClickUp" }, { id: "linear", name: "Linear" }, { id: "asana", name: "Asana" },
  ]},
  { category: "Communication", items: [
    { id: "slack", name: "Slack" }, { id: "teams-chat", name: "Teams Chat" },
  ]},
];

/* ─── Reusable input class ─── */
const inputCls = "w-full rounded-lg border border-black/[0.08] px-3 py-2 text-[13px] text-gray-900 placeholder:text-gray-400 transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20";
const textareaCls = `${inputCls} resize-none`;

/* ─── Page component ─── */

export function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [completed, setCompleted] = useState<Set<number>>(new Set());
  const [isAgency, setIsAgency] = useState(true);
  const [connectedIntegrations, setConnectedIntegrations] = useState<Set<string>>(new Set());

  // AMB-434 — controlled answers. Start BLANK (this is a brand-new external agency, not the
  // Ambid mock). The 3 foundation textareas per concept feed the seeded foundation docs.
  const [companyName, setCompanyName] = useState("");
  const [website, setWebsite] = useState("");
  const [brandStory, setBrandStory] = useState("");
  const [whatYouSell, setWhatYouSell] = useState("");
  const [goals, setGoals] = useState("");
  const [idealCustomer, setIdealCustomer] = useState("");
  const [successStory, setSuccessStory] = useState("");
  const [customerWants, setCustomerWants] = useState("");
  const [voiceTone, setVoiceTone] = useState("");
  const [workflow, setWorkflow] = useState("");
  const [team, setTeam] = useState<{ name: string; email: string; role: string }[]>([]);
  const [newMember, setNewMember] = useState<{ name: string; email: string }>({ name: "", email: "" });
  // Step 6 (agency only) — the clients the agency manages. Each becomes a client folder.
  const [clients, setClients] = useState<{ name: string; industry: string; website: string }[]>([]);
  const [newClient, setNewClient] = useState<{ name: string; industry: string; website: string }>({ name: "", industry: "", website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const wizardSteps = isAgency ? [...BASE_STEPS, AGENCY_STEP] : BASE_STEPS;
  const wi = step - 1;

  // Persist onboarding (AMB-434): create the primary client with its 3 foundation docs, invite
  // any team members, mark onboarding complete, then land on Home. Foundation answers are laid
  // into voice / company / icp docs with app-correct frontmatter server-side.
  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const clientName = (companyName || "My Workspace").trim();
      await agentsFetch("/api/agents/client/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: clientName,
          foundation: {
            company: { brand_story: brandStory, what_you_sell: whatYouSell, goals, website },
            icp: { ideal_customer: idealCustomer, best_success_story: successStory, wants_and_struggles: customerWants },
            voice: { tone: voiceTone, workflow },
          },
        }),
      });
      // Set the agency's display name from what they entered (provisioning seeds only a
      // placeholder; the manifest agencyName drives the Profile tab + the app header).
      const agencyName = companyName.trim();
      if (agencyName) {
        await agentsFetch("/api/agents/profile", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ agencyName }),
        });
      }

      // Agency clients (step 6): create a folder per client. No foundation here — each client
      // gets its own scoped onboarding later. Skipped for a direct business (no client list).
      if (isAgency) {
        for (const c of clients) {
          if (!c.name.trim()) continue;
          await agentsFetch("/api/agents/client/create", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name: c.name.trim() }),
          });
        }
      }
      for (const m of team) {
        if (!m.email) continue;
        await agentsFetch("/api/agents/members", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: m.name, email: m.email, role: m.role }),
        });
      }
      await agentsFetch("/api/agents/onboarding/complete", { method: "POST" });
      navigate("/agents/home");
    } catch (e) {
      setSubmitError("Something went wrong saving your setup. Please try again.");
      setSubmitting(false);
    }
  };

  // "Save & continue later" — there's no draft store, so this simply leaves the app the way the
  // Welcome screen promises ("come back anytime"): sign out and return to login. Onboarding
  // resumes on the next login (the workspace gate routes the owner back here until it's done).
  const saveAndContinueLater = () => {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
    } catch { /* ignore */ }
    navigate("/login");
  };

  const addMember = () => {
    if (!newMember.email.trim()) return;
    setTeam((prev) => [...prev, { name: newMember.name.trim(), email: newMember.email.trim(), role: "Team Member" }]);
    setNewMember({ name: "", email: "" });
  };

  const addClient = () => {
    if (!newClient.name.trim()) return;
    setClients((prev) => [...prev, { name: newClient.name.trim(), industry: newClient.industry.trim(), website: newClient.website.trim() }]);
    setNewClient({ name: "", industry: "", website: "" });
  };

  const markComplete = () => {
    setCompleted((prev) => new Set(prev).add(wi));
    if (wi < wizardSteps.length - 1) setStep(step + 1);
  };

  const toggleIntegration = (id: string) => {
    setConnectedIntegrations((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const isLastWizardStep = wi === wizardSteps.length - 1;

  /* ─── Navigation bar ─── */
  const navBar = (
    <div>
    {submitError && <p className="mb-3 text-[13px] text-red-600">{submitError}</p>}
    <div className="mt-10 flex items-center justify-between border-t border-black/[0.04] pt-6">
      <button
        onClick={() => setStep(step - 1)}
        className="rounded-lg border border-black/[0.06] px-4 py-2 text-[13px] font-medium text-gray-600 transition-all duration-150 hover:bg-gray-50 hover:text-gray-900"
      >
        &larr; Back
      </button>
      <div className="flex items-center gap-3">
        <button onClick={saveAndContinueLater} className="text-[13px] text-gray-400 transition-all duration-150 hover:text-gray-600">Save &amp; continue later</button>
        {isLastWizardStep ? (
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="rounded-lg bg-gray-900 px-6 py-2.5 text-[13px] font-medium text-white shadow-sm transition-all duration-150 hover:bg-gray-800 active:scale-[0.98] disabled:opacity-60"
          >
            {submitting ? "Setting up your workspace…" : "Finish setup →"}
          </button>
        ) : (
          <button
            onClick={markComplete}
            className="rounded-lg bg-gray-900 px-5 py-2.5 text-[13px] font-medium text-white shadow-sm transition-all duration-150 hover:bg-gray-800 active:scale-[0.98]"
          >
            Continue &rarr;
          </button>
        )}
      </div>
    </div>
    </div>
  );

  /* ═══════════════════════════════════════
     STEP 0: WELCOME (pre-wizard, no sidebar)
     ═══════════════════════════════════════ */
  if (step === 0) {
    return (
      <div
        className="relative flex h-screen items-center justify-center overflow-hidden bg-gradient-to-b from-gray-50 via-gray-50 to-white"
        style={{ fontFamily: "'Inter', system-ui, -apple-system, sans-serif" }}
      >
        {/* Subtle grid texture */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage: "radial-gradient(circle at 1px 1px, rgb(0 0 0 / 0.05) 1px, transparent 0)",
            backgroundSize: "24px 24px",
          }}
        />

        <div className="relative z-10 w-full max-w-lg px-8">
          {/* Logo */}
          <div className="mb-10 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-blue-700 shadow-lg shadow-blue-600/20">
            <svg className="h-7 w-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
            </svg>
          </div>

          {/* Hero typography */}
          <div className="mb-2 flex items-center gap-2.5">
            <p className="text-[13px] font-medium uppercase tracking-widest text-blue-600/80">Account Setup</p>
            <span className="text-blue-600/30">&middot;</span>
            <p className="text-[13px] font-medium uppercase tracking-widest text-gray-400">Step 0 of 6</p>
          </div>
          <h1 className="mb-3 text-3xl font-bold tracking-tight text-gray-900">Welcome to Launch OS</h1>
          <p className="mb-7 max-w-md text-[15px] leading-relaxed text-gray-500">
            Let&apos;s get your account set up so your AI agents can start working. Six short steps, about 15 minutes — save and come back anytime.
          </p>

          {/* Step-map preview */}
          <div className="mb-6 rounded-xl border border-black/[0.06] bg-white/60 p-5 backdrop-blur-sm">
            <h3 className="mb-3.5 text-[13px] font-semibold text-gray-900">The 6 steps</h3>
            <div className="space-y-2.5">
              {[
                { n: 1, label: "Company & Brand", icon: "building" },
                { n: 2, label: "Your Customers", icon: "users" },
                { n: 3, label: "Voice & Workflow", icon: "mic" },
                { n: 4, label: "Connections", icon: "link" },
                { n: 5, label: "Your Team", icon: "team" },
                { n: 6, label: "Your Clients", icon: "briefcase", note: "agency only" },
              ].map((s) => (
                <div key={s.n} className="flex items-center gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-[11px] font-semibold text-gray-500 tabular-nums">
                    {s.n}
                  </span>
                  <StepIcon type={s.icon} active={false} />
                  <span className="text-[13px] font-medium text-gray-700">{s.label}</span>
                  {s.note && (
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-400">
                      {s.note}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* How-it-works card */}
          <div className="mb-6 rounded-xl border border-blue-100/80 bg-blue-50/40 p-5 backdrop-blur-sm">
            <h3 className="mb-3.5 text-[13px] font-semibold text-blue-900">Here&apos;s how it works</h3>
            <div className="space-y-3">
              {[
                { day: "1", desc: "Complete this quick setup" },
                { day: "2", desc: "Your workspace is ready — no waiting" },
                { day: "3", desc: "Upload your first meeting — Zoom, Google Meet, or a transcript" },
                { day: "4", desc: "Your AI agents return a summary, action items, and first-draft assets in minutes" },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-blue-100/70 px-1.5 text-[11px] font-semibold text-blue-700 tabular-nums">
                    {item.day}
                  </span>
                  <span className="text-[13px] text-blue-800">{item.desc}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Quality one-liner — demoted to keep the CTA in the closing position */}
          <div className="mb-7 flex items-start gap-2.5">
            <svg className="mt-px h-4 w-4 flex-shrink-0 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
            <p className="text-xs leading-relaxed text-gray-600">
              <span className="font-semibold text-gray-700">Quality compounds:</span> the more context you give now, the better the first drafts — and the system keeps improving as it learns what you approve.
            </p>
          </div>

          <button
            onClick={() => setStep(1)}
            className="rounded-lg bg-gray-900 px-6 py-2.5 text-[13px] font-medium text-white shadow-sm transition-all duration-150 hover:bg-gray-800 active:scale-[0.98]"
          >
            Start setup &middot; ~15 min &rarr;
          </button>
        </div>
      </div>
    );
  }

  /* ═══════════════════════════════════════
     WIZARD (steps 1+, with sidebar)
     ═══════════════════════════════════════ */
  return (
    <div className="flex h-screen bg-gray-50" style={{ fontFamily: "'Inter', system-ui, -apple-system, sans-serif" }}>
      {/* ─── Left sidebar ─── */}
      <div className="flex w-[260px] flex-col border-r border-black/[0.06] bg-white">
        {/* Logo header */}
        <div className="border-b border-black/[0.06] px-6 py-5">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-blue-700 text-xs font-bold text-white shadow-sm">
              L
            </div>
            <div>
              <p className="text-[13px] font-semibold text-gray-900">Launch OS</p>
              <p className="text-[11px] text-gray-400">Account Setup</p>
            </div>
          </div>
        </div>

        {/* Step list */}
        <div className="flex-1 px-3 py-4">
          <div className="space-y-0.5">
            {wizardSteps.map((s, i) => {
              const isDone = completed.has(i);
              const isCurrent = wi === i;
              return (
                <button
                  key={s.key}
                  onClick={() => setStep(i + 1)}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all duration-150 ${
                    isCurrent
                      ? "bg-gray-900/[0.04] text-gray-900"
                      : isDone
                      ? "text-gray-600 hover:bg-gray-50"
                      : "text-gray-400 hover:bg-gray-50"
                  }`}
                >
                  {/* Step indicator */}
                  <div
                    className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium transition-all duration-150 ${
                      isDone
                        ? "bg-emerald-500 text-white"
                        : isCurrent
                        ? "bg-gray-900 text-white"
                        : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    {isDone ? (
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                    ) : (
                      i + 1
                    )}
                  </div>
                  <span className="text-[13px] font-medium">{s.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Progress footer */}
        <div className="border-t border-black/[0.06] px-6 py-4">
          <div className="mb-2 flex items-center justify-between text-xs text-gray-500">
            <span>{completed.size} of {wizardSteps.length} complete</span>
            <span className="tabular-nums">{Math.round((completed.size / wizardSteps.length) * 100)}%</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-1.5 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500 ease-out"
              style={{ width: `${(completed.size / wizardSteps.length) * 100}%` }}
            />
          </div>
          <p className="mt-3 text-[11px] text-gray-400">
            ~{Math.max(1, (wizardSteps.length - completed.size) * 3)} min remaining
          </p>
        </div>
      </div>

      {/* ─── Right content area ─── */}
      <div className="flex flex-1 flex-col overflow-auto">
        <div className="mx-auto w-full max-w-2xl px-8 py-10">

          {/* ═══ STEP 1: COMPANY & BRAND ═══ */}
          {step === 1 && (
            <div>
              <div className="mb-1.5 flex items-center gap-2">
                <StepIcon type="building" active />
                <p className="text-xs font-medium uppercase tracking-widest text-blue-600/80">
                  Step 1 of {wizardSteps.length}
                </p>
              </div>
              <h1 className="mb-1 text-xl font-bold tracking-tight text-gray-900">Company &amp; Brand</h1>
              <p className="mb-8 text-[13px] leading-relaxed text-gray-500">
                Basic information about your business and what you do.
              </p>

              <div className="space-y-6">
                {/* Business type toggle */}
                <div>
                  <label className="mb-2 block text-[13px] font-medium text-gray-700">What best describes your business?</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setIsAgency(true)}
                      className={`rounded-xl border px-4 py-3.5 text-left transition-all duration-150 ${
                        isAgency
                          ? "border-blue-300 bg-blue-50/60 ring-1 ring-blue-200 shadow-sm"
                          : "border-black/[0.06] hover:border-black/[0.12] hover:shadow-sm"
                      }`}
                    >
                      <p className={`text-[13px] font-medium ${isAgency ? "text-blue-900" : "text-gray-700"}`}>
                        Agency / Consultancy
                      </p>
                      <p className={`mt-0.5 text-xs ${isAgency ? "text-blue-600" : "text-gray-400"}`}>
                        I manage marketing for multiple clients
                      </p>
                    </button>
                    <button
                      onClick={() => setIsAgency(false)}
                      className={`rounded-xl border px-4 py-3.5 text-left transition-all duration-150 ${
                        !isAgency
                          ? "border-blue-300 bg-blue-50/60 ring-1 ring-blue-200 shadow-sm"
                          : "border-black/[0.06] hover:border-black/[0.12] hover:shadow-sm"
                      }`}
                    >
                      <p className={`text-[13px] font-medium ${!isAgency ? "text-blue-900" : "text-gray-700"}`}>
                        Direct Business
                      </p>
                      <p className={`mt-0.5 text-xs ${!isAgency ? "text-blue-600" : "text-gray-400"}`}>
                        I market my own company
                      </p>
                    </button>
                  </div>
                </div>

                {/* Company name & website */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block text-[13px] font-medium text-gray-700">Company name</label>
                    <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Acme Agency" className={inputCls} />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-[13px] font-medium text-gray-700">Website</label>
                    <input type="text" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="acme.com" className={inputCls} />
                  </div>
                </div>

                {/* Brand story */}
                <div>
                  <label className="mb-1.5 block text-[13px] font-medium text-gray-700">Brand story &amp; niche</label>
                  <p className="mb-1.5 text-xs text-gray-400">What your company does, your history, and the niche or industry you serve.</p>
                  <textarea
                    rows={3}
                    value={brandStory}
                    onChange={(e) => setBrandStory(e.target.value)}
                    placeholder="What your company does, your history, and the niche or industry you serve."
                    className={textareaCls}
                  />
                </div>

                {/* What you sell */}
                <div>
                  <label className="mb-1.5 block text-[13px] font-medium text-gray-700">What you sell</label>
                  <p className="mb-1.5 text-xs text-gray-400">Your products or services, how they&apos;re delivered, and at what price points.</p>
                  <textarea
                    rows={3}
                    value={whatYouSell}
                    onChange={(e) => setWhatYouSell(e.target.value)}
                    placeholder="Your products or services, how they're delivered, and at what price points."
                    className={textareaCls}
                  />
                </div>

                {/* Goals */}
                <div>
                  <label className="mb-1.5 block text-[13px] font-medium text-gray-700">Goals with Launch OS</label>
                  <p className="mb-1.5 text-xs text-gray-400">What&apos;s the dream outcome? What results are you aiming for, and how will you know it&apos;s working?</p>
                  <textarea
                    rows={3}
                    value={goals}
                    onChange={(e) => setGoals(e.target.value)}
                    placeholder="The dream outcome and how you'll know it's working."
                    className={textareaCls}
                  />
                </div>

                {/* Brand guide upload */}
                <div>
                  <label className="mb-1.5 block text-[13px] font-medium text-gray-700">
                    Brand guide or reference <span className="font-normal text-gray-400">(optional)</span>
                  </label>
                  <p className="mb-1.5 text-xs text-gray-400">Have a brand guide or reference materials? Add the key points to your brand story above — you can refine it anytime.</p>
                </div>
              </div>

              {/* AI info card */}
              <div className="mt-8 rounded-xl border border-blue-100/80 bg-blue-50/40 p-4">
                <div className="flex items-start gap-3">
                  <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                  </svg>
                  <div>
                    <p className="text-[13px] font-medium text-blue-900">AI will expand this</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-blue-700">Your answers will be processed into a detailed company profile that guides every draft. You can refine it anytime from your workspace.</p>
                  </div>
                </div>
              </div>

              {navBar}
            </div>
          )}

          {/* ═══ STEP 2: YOUR CUSTOMERS ═══ */}
          {step === 2 && (
            <div>
              <div className="mb-1.5 flex items-center gap-2">
                <StepIcon type="users" active />
                <p className="text-xs font-medium uppercase tracking-widest text-blue-600/80">
                  Step 2 of {wizardSteps.length}
                </p>
              </div>
              <h1 className="mb-1 text-xl font-bold tracking-tight text-gray-900">Your Customers</h1>
              <p className="mb-8 text-[13px] leading-relaxed text-gray-500">
                Tell us about your ideal customer so our AI can speak to them effectively. Open-ended is fine — write in your own words.
              </p>

              <div className="space-y-6">
                <div>
                  <label className="mb-1.5 block text-[13px] font-medium text-gray-700">Who is your ideal customer?</label>
                  <p className="mb-1.5 text-xs text-gray-400">Picture your bullseye customer. Describe demographics, company size, role, location, income level — whatever paints the picture.</p>
                  <textarea
                    rows={4}
                    value={idealCustomer}
                    onChange={(e) => setIdealCustomer(e.target.value)}
                    placeholder="Demographics, company size, role, location — whatever paints the picture."
                    className={textareaCls}
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-[13px] font-medium text-gray-700">Your best success story</label>
                  <p className="mb-1.5 text-xs text-gray-400">Think of your greatest success — a customer you&apos;d want ten more of. What results did they achieve with you, and what positive changes followed?</p>
                  <textarea
                    rows={4}
                    value={successStory}
                    onChange={(e) => setSuccessStory(e.target.value)}
                    placeholder="A customer you'd want ten more of — the results they achieved."
                    className={textareaCls}
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-[13px] font-medium text-gray-700">What do your customers want and struggle with?</label>
                  <p className="mb-1.5 text-xs text-gray-400">Their frustrations, desires, beliefs, and what they&apos;re trying to gain, become, or avoid.</p>
                  <textarea
                    rows={4}
                    value={customerWants}
                    onChange={(e) => setCustomerWants(e.target.value)}
                    placeholder="Their frustrations, desires, and what they're trying to gain or avoid."
                    className={textareaCls}
                  />
                </div>
              </div>

              {/* AI info card */}
              <div className="mt-8 rounded-xl border border-blue-100/80 bg-blue-50/40 p-4">
                <div className="flex items-start gap-3">
                  <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                  </svg>
                  <div>
                    <p className="text-[13px] font-medium text-blue-900">AI will expand this</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-blue-700">Your open-ended answers will be processed into a detailed customer profile. It deepens over time as your agents learn what you approve.</p>
                  </div>
                </div>
              </div>

              {navBar}
            </div>
          )}

          {/* ═══ STEP 3: VOICE & WORKFLOW ═══ */}
          {step === 3 && (
            <div>
              <div className="mb-1.5 flex items-center gap-2">
                <StepIcon type="mic" active />
                <p className="text-xs font-medium uppercase tracking-widest text-blue-600/80">
                  Step 3 of {wizardSteps.length}
                </p>
              </div>
              <h1 className="mb-1 text-xl font-bold tracking-tight text-gray-900">Voice &amp; Workflow</h1>
              <p className="mb-8 text-[13px] leading-relaxed text-gray-500">
                How you sound and how you work. The more voice samples you share, the better our AI reproduces your style from day one.
              </p>

              <div className="space-y-6">
                {/* Voice description */}
                <div>
                  <label className="mb-1.5 block text-[13px] font-medium text-gray-700">How would you describe your brand&apos;s voice and tone?</label>
                  <textarea
                    rows={3}
                    value={voiceTone}
                    onChange={(e) => setVoiceTone(e.target.value)}
                    placeholder="e.g. Professional but approachable; plain-spoken, confident, warm."
                    className={textareaCls}
                  />
                </div>

                {/* Voice samples — file upload is deferred (AMB-335); captured via the text field for now */}
                <div>
                  <label className="mb-1.5 block text-[13px] font-medium text-gray-700">
                    Voice samples <span className="font-normal text-gray-400">(optional)</span>
                  </label>
                  <p className="text-xs text-gray-400">Emails, blog posts, case studies — anything that shows how your primary writer sounds. Capture the key traits in the voice description above for now; the more detail, the better your agents match your style.</p>
                </div>

                <div className="border-t border-black/[0.04]" />

                {/* Workflow */}
                <div>
                  <label className="mb-1.5 block text-[13px] font-medium text-gray-700">How does an idea go live at your company?</label>
                  <p className="mb-1.5 text-xs text-gray-400">Walk us through step by step — from someone having an idea (a campaign, an email, a launch) to it going live.</p>
                  <textarea
                    rows={4}
                    value={workflow}
                    onChange={(e) => setWorkflow(e.target.value)}
                    placeholder="From someone having an idea to it going live — step by step."
                    className={textareaCls}
                  />
                </div>

                {/* Competitors */}
                <div>
                  <label className="mb-1.5 block text-[13px] font-medium text-gray-700">Top 3-5 competitors</label>
                  <p className="mb-1.5 text-xs text-gray-400">Who do you compete with? This helps our AI differentiate your positioning.</p>
                  <div className="space-y-2">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="flex h-5 w-5 items-center justify-center rounded bg-gray-100 text-[10px] font-semibold text-gray-500 tabular-nums">
                          {i + 1}
                        </span>
                        <input type="text" placeholder="Competitor name" className={`flex-1 ${inputCls}`} />
                      </div>
                    ))}
                    <button className="flex items-center gap-1.5 text-xs font-medium text-blue-600 transition-all duration-150 hover:text-blue-700">
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                      </svg>
                      Add another
                    </button>
                  </div>
                </div>
              </div>

              {navBar}
            </div>
          )}

          {/* ═══ STEP 4: CONNECTIONS ═══ */}
          {step === 4 && (
            <div>
              <div className="mb-1.5 flex items-center gap-2">
                <StepIcon type="link" active />
                <p className="text-xs font-medium uppercase tracking-widest text-blue-600/80">
                  Step 4 of {wizardSteps.length}
                </p>
              </div>
              <h1 className="mb-1 text-xl font-bold tracking-tight text-gray-900">Connections</h1>
              <p className="mb-6 text-[13px] leading-relaxed text-gray-500">
                Connect the tools you use. We&apos;ll configure everything automatically — customize later in Settings.
              </p>

              {/* Integration cards */}
              <div className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-black/[0.04] divide-y divide-black/[0.04]">
                {INTEGRATIONS.map((group) => (
                  <div key={group.category} className="px-5 py-4">
                    <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-widest text-gray-400">
                      {group.category}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {group.items.map((item) => {
                        const connected = connectedIntegrations.has(item.id);
                        return (
                          <button
                            key={item.id}
                            onClick={() => toggleIntegration(item.id)}
                            className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[13px] font-medium transition-all duration-150 ${
                              connected
                                ? "border-emerald-200 bg-emerald-50/80 text-emerald-700 shadow-sm"
                                : "border-black/[0.06] text-gray-600 hover:border-black/[0.12] hover:bg-gray-50 hover:shadow-sm"
                            }`}
                          >
                            {connected && (
                              <svg className="h-3.5 w-3.5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                              </svg>
                            )}
                            {item.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-gray-400">Don&apos;t use any of these? No problem — you can connect tools anytime from Settings, or just upload meeting transcripts directly.</p>

              {/* Slack email */}
              <div className="mt-6">
                <label className="mb-1.5 block text-[13px] font-medium text-gray-700">Your Slack email</label>
                <p className="mb-1.5 text-xs text-gray-400">We&apos;ll invite you to a dedicated Launch OS channel.</p>
                <input type="email" placeholder="you@youragency.com" className={inputCls} />
              </div>

              {/* Consent checkbox */}
              <div className="mt-6 rounded-xl bg-gray-50 px-4 py-3.5 ring-1 ring-black/[0.04]">
                <div className="flex items-start gap-2.5">
                  <div className="mt-0.5 flex h-4 w-4 items-center justify-center rounded border border-blue-500 bg-blue-500 transition-all duration-150">
                    <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  </div>
                  <p className="text-xs leading-relaxed text-gray-500">
                    By continuing, you confirm that you record meetings with appropriate consent from participants and that you may share recordings and documents with us.
                  </p>
                </div>
              </div>

              {navBar}
            </div>
          )}

          {/* ═══ STEP 5: YOUR TEAM ═══ */}
          {step === 5 && (
            <div>
              <div className="mb-1.5 flex items-center gap-2">
                <StepIcon type="team" active />
                <p className="text-xs font-medium uppercase tracking-widest text-blue-600/80">
                  Step 5 of {wizardSteps.length}
                </p>
              </div>
              <h1 className="mb-1 text-xl font-bold tracking-tight text-gray-900">Your Team</h1>
              <p className="mb-8 text-[13px] leading-relaxed text-gray-500">
                Add the people who&apos;ll be using Launch OS. You can always add more later.
              </p>

              <div className="space-y-2.5">
                {team.map((member, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-4 rounded-xl bg-white px-4 py-3.5 shadow-sm ring-1 ring-black/[0.04] transition-all duration-150 hover:shadow"
                  >
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100 text-[13px] font-bold text-gray-500">
                      {(member.name || member.email).split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-medium text-gray-900">{member.name || member.email}</p>
                      <p className="text-xs text-gray-400">{member.email}</p>
                    </div>
                    <button
                      onClick={() => setTeam((prev) => prev.filter((_, j) => j !== i))}
                      className="rounded-md px-2 py-0.5 text-[11px] font-medium text-gray-400 transition-all duration-150 hover:bg-gray-100 hover:text-gray-700"
                    >
                      Remove
                    </button>
                  </div>
                ))}

                {/* Add team member */}
                <div className="flex items-center gap-2 rounded-xl border-2 border-dashed border-black/[0.06] p-2.5">
                  <input
                    type="text"
                    value={newMember.name}
                    onChange={(e) => setNewMember((m) => ({ ...m, name: e.target.value }))}
                    placeholder="Name"
                    className={`flex-1 ${inputCls}`}
                  />
                  <input
                    type="email"
                    value={newMember.email}
                    onChange={(e) => setNewMember((m) => ({ ...m, email: e.target.value }))}
                    placeholder="email@company.com"
                    onKeyDown={(e) => { if (e.key === "Enter") addMember(); }}
                    className={`flex-1 ${inputCls}`}
                  />
                  <button
                    onClick={addMember}
                    className="flex items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-2 text-[13px] font-medium text-white transition-all duration-150 hover:bg-gray-800"
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Info box */}
              <div className="mt-6 rounded-xl bg-gray-50 p-4 ring-1 ring-black/[0.04]">
                <p className="text-xs leading-relaxed text-gray-500">Each team member gets their own login. Admins can manage settings and approve deliverables. Team Members can view, create, and route work.</p>
              </div>

              {/* Completion card (non-agency) */}
              {!isAgency && (
                <div className="mt-6 rounded-xl border border-emerald-100/80 bg-emerald-50/40 p-5">
                  <div className="flex items-start gap-3">
                    <svg className="mt-0.5 h-6 w-6 flex-shrink-0 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <h2 className="text-[13px] font-semibold text-emerald-900">You&apos;re almost done!</h2>
                      <p className="mt-1 text-xs leading-relaxed text-emerald-700">When you finish, your workspace is ready right away. Head to Meetings and upload your first meeting — your AI agents turn it into a summary, action items, and first-draft assets within minutes.</p>
                    </div>
                  </div>
                </div>
              )}

              {navBar}
            </div>
          )}

          {/* ═══ STEP 6: YOUR CLIENTS (agency only) ═══ */}
          {isAgency && step === 6 && (
            <div>
              <div className="mb-1.5 flex items-center gap-2">
                <StepIcon type="briefcase" active />
                <p className="text-xs font-medium uppercase tracking-widest text-blue-600/80">
                  Step 6 of {wizardSteps.length}
                </p>
              </div>
              <h1 className="mb-1 text-xl font-bold tracking-tight text-gray-900">Your Clients</h1>
              <p className="mb-8 text-[13px] leading-relaxed text-gray-500">
                Tell us about the clients you&apos;ll be running through Launch OS. Just enough to get started — you can add more later.
              </p>

              <div className="space-y-2.5">
                {clients.map((client, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-4 rounded-xl bg-white px-4 py-3.5 shadow-sm ring-1 ring-black/[0.04] transition-all duration-150 hover:shadow"
                  >
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100 text-[13px] font-bold text-gray-500">
                      {client.name[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-medium text-gray-900">{client.name}</p>
                      <p className="text-xs text-gray-400">{[client.industry, client.website].filter(Boolean).join(" · ") || "Client"}</p>
                    </div>
                    <button
                      onClick={() => setClients((prev) => prev.filter((_, j) => j !== i))}
                      className="rounded-md px-2 py-0.5 text-[11px] font-medium text-gray-400 transition-all duration-150 hover:bg-gray-100 hover:text-gray-700"
                    >
                      Remove
                    </button>
                  </div>
                ))}

                {/* Add client */}
                <div className="flex items-center gap-2 rounded-xl border-2 border-dashed border-black/[0.06] p-2.5">
                  <input
                    type="text"
                    value={newClient.name}
                    onChange={(e) => setNewClient((c) => ({ ...c, name: e.target.value }))}
                    placeholder="Client name"
                    className={`flex-1 ${inputCls}`}
                  />
                  <input
                    type="text"
                    value={newClient.industry}
                    onChange={(e) => setNewClient((c) => ({ ...c, industry: e.target.value }))}
                    placeholder="Industry"
                    className={`flex-1 ${inputCls}`}
                  />
                  <input
                    type="text"
                    value={newClient.website}
                    onChange={(e) => setNewClient((c) => ({ ...c, website: e.target.value }))}
                    placeholder="Website"
                    onKeyDown={(e) => { if (e.key === "Enter") addClient(); }}
                    className={`flex-1 ${inputCls}`}
                  />
                  <button
                    onClick={addClient}
                    className="flex items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-2 text-[13px] font-medium text-white transition-all duration-150 hover:bg-gray-800"
                  >
                    Add
                  </button>
                </div>
                {clients.length === 0 && (
                  <p className="px-1 text-xs text-gray-400">No clients yet — add your first above, or skip and add them later in Settings.</p>
                )}
              </div>

              {/* Info box */}
              <div className="mt-6 rounded-xl bg-gray-50 p-4 ring-1 ring-black/[0.04]">
                <p className="text-xs leading-relaxed text-gray-500">Each client gets their own sub-workspace with a scoped onboarding wizard for their company profile, ICP, and voice.</p>
              </div>

              {/* Completion card */}
              <div className="mt-6 rounded-xl border border-emerald-100/80 bg-emerald-50/40 p-5">
                <div className="flex items-start gap-3">
                  <svg className="mt-0.5 h-6 w-6 flex-shrink-0 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h2 className="text-[13px] font-semibold text-emerald-900">You&apos;re almost done!</h2>
                    <p className="mt-1 text-xs leading-relaxed text-emerald-700">When you finish, your workspace is ready right away. Head to Meetings and upload your first meeting — your AI agents turn it into a summary, action items, and first-draft assets within minutes.</p>
                  </div>
                </div>
              </div>

              {navBar}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
